
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOMetadata from '@/components/SEOMetadata';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOMetadata
        title="Refund Policy | NEXX TELECOM"
        description="NEXX TELECOM Refund Policy for VPS servers, proxy services, consulting, and other infrastructure solutions. Clear guidelines for refund eligibility."
        keywords="refund policy, money back guarantee, service refunds"
        canonical="https://nexx.net/refund-policy"
        robots="noindex, nofollow"
      />
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-8">
            <Link to="/">
              <Button variant="ghost" className="text-nexx-green hover:text-green-700">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center">
              <span className="mr-3">💵</span>
              NEXX TELECOM – Refund Policy
            </h1>
            <p className="text-lg text-gray-600">
              Effective Date: June 4th, 2025
            </p>
            <p className="text-sm text-gray-500">
              Last Updated: June 4th, 2025
            </p>
            <div className="w-24 h-px bg-gray-300 mx-auto mt-6"></div>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-8">
              At NEXX TELECOM, our goal is to provide reliable, customized infrastructure and connectivity solutions. Due to the nature of our services—many of which involve provisioning custom network assets, digital infrastructure, or limited-use resources—we maintain a strict but fair refund policy outlined below.
            </p>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* General Policy */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">1. General Policy</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>All sales are final once a service has been provisioned or delivered.</li>
                <li>Refunds are only considered in cases of non-delivery, service malfunction, or administrative error.</li>
                <li>We do not offer refunds for misuse, misconfiguration by the client, or violations of our Terms of Service.</li>
              </ul>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Service-Based Refund Guidelines */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">2. Service-Based Refund Guidelines</h2>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">A. Consulting & Advisory Services</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Refunds:</strong> Not applicable once the consultation has occurred.</li>
                  <li><strong>No-shows:</strong> No refunds for missed or rescheduled consultations with less than 24 hours' notice.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">B. Proxy & VPN Services</h3>
                <div className="mb-4">
                  <strong className="text-gray-800">Refund Eligibility:</strong>
                  <p className="text-gray-700 mt-2">Refunds may be issued only if:</p>
                </div>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Service was not provisioned within the agreed timeframe (usually within 72 hours)</li>
                  <li>The delivered proxy/VPN does not function due to a fault on our end, and we fail to remedy it within 48 hours</li>
                  <li><strong>Pro-rata refunds:</strong> Not offered for partial usage or expiration of monthly terms</li>
                  <li>See: <Link to="/proxy-tos" className="text-nexx-green hover:underline">Proxy Terms of Service</Link></li>
                </ul>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">C. VPS & Bare Metal Servers</h3>
                <p className="text-gray-700 mb-2"><strong>Refunds:</strong> Only granted if:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                  <li>Provisioning failure occurs and cannot be resolved within 3 business days</li>
                  <li>Server is delivered inoperable with no client-side misconfiguration, and we are unable to replace or repair</li>
                </ul>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Upfront payments:</strong> Non-refundable once server is delivered</li>
                  <li><strong>Hardware or abuse-related suspensions:</strong> Not eligible for refunds</li>
                  <li>See: <Link to="/server-tos" className="text-nexx-green hover:underline">Server Terms of Service</Link></li>
                </ul>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Chargebacks */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">3. Chargebacks</h2>
              <p className="text-gray-700">
                All chargebacks or payment disputes will result in immediate suspension of associated services. If you are experiencing an issue, contact us first at{' '}
                <a href="mailto:billing@nexx.net" className="text-nexx-green hover:underline">billing@nexx.net</a>
                {' '}— we're committed to resolving legitimate concerns.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Custom Requests */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">4. Custom Requests</h2>
              <p className="text-gray-700">
                For custom-built solutions or special-use cases (e.g. IP leasing, custom routing, proxy pools), refunds are only considered in cases of outright failure to deliver and are reviewed individually.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* How to Request */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">5. How to Request a Refund</h2>
              <p className="text-gray-700 mb-4">
                To request a refund, send an email to{' '}
                <a href="mailto:support@nexx.net" className="text-nexx-green hover:underline">support@nexx.net</a>
                {' '}with:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Your full name or company name</li>
                <li>Service ID or order reference</li>
                <li>Reason for the request</li>
                <li>Screenshots or logs (if applicable)</li>
              </ul>
              <p className="text-gray-700 mt-4">
                All requests are reviewed within 3–5 business days.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Footer Note */}
            <div className="mb-12 text-center">
              <p className="text-gray-700">
                This policy is designed to ensure clarity and fairness for both NEXX TELECOM and its clients. By purchasing or using our services, you acknowledge and accept the terms outlined above.
              </p>
              <div className="mt-6">
                <Button className="bg-nexx-green hover:bg-green-700 text-white">
                  <a href="mailto:support@nexx.net">Contact Support</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RefundPolicy;
